import { useEffect, useRef, useState } from "react";
import { PURSUIT_DURATION_MS, PURSUIT_LEAD_IN_MS, pursuitTarget } from "@gazelab/analysis";
import { Actions, ErrorText, Panel } from "../ui.js";
import { NEUTRAL_BG } from "./Presentation.js";
import { errorMessage } from "../../../services/api.js";

/**
 * Validación con punto en movimiento: el punto aparece quieto (para encontrarlo con la mirada) y
 * luego recorre la pantalla durante 12 s. Mide el retraso y el error de la mirada estimada.
 * La captura la gestiona la página: `onStart` inicia la captura y devuelve su t0; `onFinish` la
 * detiene, calcula y envía las métricas.
 */
export function PursuitScreen({ onStart, onFinish }: { onStart: () => Promise<number>; onFinish: () => Promise<void> }) {
  const [phase, setPhase] = useState<"intro" | "running" | "saving">("intro");
  const [error, setError] = useState<string | null>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const cbs = useRef({ onStart, onFinish });
  cbs.current = { onStart, onFinish };

  useEffect(() => {
    if (phase !== "running") return;
    let raf = 0;
    let cancelled = false;
    const place = (tMs: number) => {
      const p = pursuitTarget(tMs, window.innerWidth, window.innerHeight);
      if (dotRef.current) dotRef.current.style.transform = `translate(${p.x}px, ${p.y}px)`;
    };
    place(0);
    (async () => {
      const t0 = await cbs.current.onStart();
      const tick = () => {
        if (cancelled) return;
        const elapsed = performance.now() - t0;
        place(elapsed - PURSUIT_LEAD_IN_MS);
        if (elapsed >= PURSUIT_LEAD_IN_MS + PURSUIT_DURATION_MS) {
          setPhase("saving");
          cbs.current.onFinish().catch((e) => {
            setError(errorMessage(e));
            setPhase("intro");
          });
          return;
        }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    })().catch((e) => {
      setError(errorMessage(e));
      setPhase("intro");
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [phase]);

  if (phase === "intro") {
    return (
      <Panel title="Sigue el punto">
        <p>
          Verás un <strong>punto</strong> que primero estará quieto y después se moverá lentamente por la pantalla durante unos
          12 segundos. <strong>Síguelo con la mirada</strong> todo el tiempo, sin mover la cabeza.
        </p>
        <p className="text-sm text-slate-500">Sirve para medir con qué precisión y retraso la cámara estima tu mirada. No hay que pulsar nada.</p>
        {error && <ErrorText>{error}</ErrorText>}
        <Actions>
          <button type="button" className="btn-primary" onClick={() => setPhase("running")} data-testid="pursuit-start">
            Comenzar
          </button>
        </Actions>
      </Panel>
    );
  }
  return (
    <div className="fixed inset-0" style={{ background: NEUTRAL_BG, cursor: "none" }} data-testid="pursuit-screen">
      {phase === "running" && (
        <div
          ref={dotRef}
          aria-hidden
          style={{
            position: "absolute",
            left: -12,
            top: -12,
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: "#111",
            boxShadow: "0 0 0 4px rgba(255,255,255,.85)",
            willChange: "transform",
          }}
        />
      )}
    </div>
  );
}
